import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  TripOrigin as CurrentIcon
} from '@mui/icons-material';

/**
 * GeneratingProgress
 * Displays a confidence-building progress experience during career plan generation.
 * Includes rotating status messages and the quality disclaimer.
 */
export default function GeneratingProgress({ targetRole = 'your target role', startTime = Date.now() }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Progress steps that users see
  const steps = [
    {
      label: 'Analyzing your background and experience',
      duration: 4000 // 4 seconds
    },
    {
      label: 'Researching current job market data',
      duration: 5000
    },
    {
      label: `Mapping employer requirements for ${targetRole}`,
      duration: 6000
    },
    {
      label: 'Optimizing fastest path to qualification',
      duration: 5000
    },
    {
      label: 'Auditing plan for accuracy and quality',
      duration: 8000
    }
  ];

  const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);

  useEffect(() => {
    // Simulate progress
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(95, (elapsed / totalDuration) * 100);
      setProgress(newProgress);

      // Determine current step
      let accumulatedTime = 0;
      for (let i = 0; i < steps.length; i++) {
        accumulatedTime += steps[i].duration;
        if (elapsed < accumulatedTime) {
          setCurrentStep(i);
          break;
        }
        if (i === steps.length - 1) {
          setCurrentStep(steps.length - 1);
        }
      }
    }, 200);

    return () => clearInterval(interval);
  }, [startTime, totalDuration]);

  const getStepIcon = (index) => {
    if (index < currentStep) {
      return <CheckIcon sx={{ color: '#22c55e', fontSize: 20 }} />;
    }
    if (index === currentStep) {
      return <CurrentIcon sx={{ color: '#00fff5', fontSize: 20, animation: 'pulse 1.5s infinite' }} />;
    }
    return <PendingIcon sx={{ color: '#525252', fontSize: 20 }} />;
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: 700, 
      mx: 'auto', 
      py: 4 
    }}>
      {/* Main Progress Card */}
      <Paper sx={{ 
        p: 4, 
        bgcolor: '#0a0a0a', 
        border: '1px solid #262626',
        borderRadius: 2,
        mb: 3
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#fafafa', 
            fontWeight: 600, 
            textAlign: 'center',
            mb: 3,
            fontFamily: 'monospace'
          }}
        >
          Building Your Personalized Career Plan
        </Typography>

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#262626',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#00fff5',
                borderRadius: 4
              }
            }}
          />
          <Typography 
            sx={{ 
              color: '#737373', 
              fontSize: '0.875rem', 
              textAlign: 'right',
              mt: 0.5 
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>

        {/* Steps List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {steps.map((step, index) => (
            <Box 
              key={index}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                opacity: index <= currentStep ? 1 : 0.5,
                transition: 'opacity 0.3s'
              }}
            >
              {getStepIcon(index)}
              <Typography 
                sx={{ 
                  color: index === currentStep ? '#fafafa' : 
                         index < currentStep ? '#22c55e' : '#737373',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }}
              >
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Disclaimer Card */}
      <Paper sx={{ 
        p: 3, 
        bgcolor: 'rgba(0,255,245,0.02)', 
        border: '1px solid rgba(0,255,245,0.1)',
        borderRadius: 2
      }}>
        <Typography 
          sx={{ 
            color: '#a3a3a3', 
            fontSize: '0.875rem',
            lineHeight: 1.7,
            fontFamily: 'system-ui'
          }}
        >
          <strong style={{ color: '#fafafa' }}>Artificial Intelligence</strong> is used in the 
          creation of this plan. Each decision is backed by employer data that tells us what 
          you need in order to qualify.
        </Typography>
        
        <Typography 
          sx={{ 
            color: '#a3a3a3', 
            fontSize: '0.875rem',
            lineHeight: 1.7,
            mt: 2,
            fontFamily: 'system-ui'
          }}
        >
          We're not guessing and we're not hallucinating results. If any of our quality checks 
          fail, the plan will not be provided.
        </Typography>
        
        <Typography 
          sx={{ 
            color: '#a3a3a3', 
            fontSize: '0.875rem',
            lineHeight: 1.7,
            mt: 2,
            fontFamily: 'system-ui'
          }}
        >
          We'll never tell you to spend 4 or 8 years in school. We specialize in{' '}
          <strong style={{ color: '#00fff5' }}>rapid transition plans</strong> that move 
          careers forward.
        </Typography>
      </Paper>

      {/* Pulsing animation style */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}
      </style>
    </Box>
  );
}

